import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearch } from "@tanstack/react-router";
import type {
  PlanPreviewItem,
  PlanPreviewResponse,
  PlanResponse,
  PlansResponse,
  PlanStatus,
  PlanSummary,
  SurahsResponse,
} from "@mubin/contracts";
import { Badge, Empty, ErrorNote, Field, Loading, Modal, PageTitle, useSubmit } from "../components/ui";
import { api, ApiError, errorText } from "../lib/api";
import { POLICY_META, formatPolicyValue, unitLabel, weekdayLabel } from "../lib/policies";
import { WEEKDAYS, type AmountUnit, type PolicyKey, type Weekday } from "@mubin/contracts";

const kindLabel: Record<PlanPreviewItem["kind"], string> = {
  new: "حفظ جديد",
  near_review: "مراجعة قريبة",
  far_review: "مراجعة بعيدة",
};
const kindTone = { new: "brand", near_review: "info", far_review: "neutral" } as const;

const today = () => new Date().toISOString().slice(0, 10);
const WEEKDAY_BY_UTC_DAY: Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const dayName = (d: string) => weekdayLabel[WEEKDAY_BY_UTC_DAY[new Date(`${d}T00:00:00Z`).getUTCDay()]!];

const statusLabel: Record<PlanStatus, string> = {
  draft: "مسودة",
  approved: "معتمدة",
  superseded: "سابقة",
  withdrawn: "مسحوبة",
};
const statusTone = {
  draft: "warning",
  approved: "success",
  superseded: "neutral",
  withdrawn: "neutral",
} as const;

/**
 * A student's plan: preview it from their circle's policies (Phase 4.2), save it as a draft and approve it
 * (Phase 4.3). The approved plan is what the teacher and the student see day by day.
 */
export function PlanPreviewPage() {
  const { enrollmentId } = useSearch({ from: "/app/plan-preview" });
  const surahs = useQuery({
    queryKey: ["surahs"],
    queryFn: () => api<SurahsResponse>("/v1/quran/surahs"),
    staleTime: Infinity,
  });
  const [form, setForm] = useState({
    startSurah: 114,
    startAyah: 1,
    direction: "" as "" | "backward" | "forward",
    /** 0: the default end (Al-Baqarah backward, An-Nas forward). */
    endSurah: 0,
    memorizedFrom: 0,
    from: today(),
    days: 14,
  });
  // A program of the student's own over the circle's policies (owner request 2026-10-26).
  const [own, setOwn] = useState(false);
  const [custom, setCustom] = useState({
    unit: "page" as AmountUnit,
    dailyAmount: 1,
    near: 3,
    nearUnit: "page" as AmountUnit,
    far: 5,
    farUnit: "page" as AmountUnit,
    studyDays: ["sun", "mon", "tue", "wed", "thu"] as Weekday[],
  });
  const { busy, error, run, setError } = useSubmit();
  const [plan, setPlan] = useState<PlanPreviewResponse | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  // The input of the plan on screen, saved as is when the supervisor keeps it.
  const [shown, setShown] = useState<object | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const qc = useQueryClient();
  const plans = useQuery({
    queryKey: ["plans", enrollmentId],
    queryFn: () => api<PlansResponse>(`/v1/plans?enrollmentId=${enrollmentId}`),
  });
  const save = useSubmit();
  const [approving, setApproving] = useState<PlanSummary | null>(null);
  const resume = useSubmit();

  function saveDraft() {
    if (!shown) return;
    void save.run(async () => {
      const { plan: saved } = await api<PlanResponse>("/v1/plans", { method: "POST", json: shown });
      setSavedId(saved.id);
      await qc.invalidateQueries({ queryKey: ["plans", enrollmentId] });
    }, errorText);
  }

  const list = surahs.data?.surahs ?? [];
  const maxAyah = list.find((s) => s.id === form.startSurah)?.ayahCount ?? 286;

  function submit(e: FormEvent) {
    e.preventDefault();
    setMissing([]);
    void run(async () => {
      // "Memorized before": from the chosen surah to An-Nas (backward plans start from the end).
      const memorizedSurahs =
        form.memorizedFrom > 0
          ? Array.from({ length: 115 - form.memorizedFrom }, (_, i) => form.memorizedFrom + i)
          : [];
      const end = list.find((x) => x.id === form.endSurah);
      // The plan itself has no duration (it runs until its range is done); `days` only sizes the preview.
      const json = {
        enrollmentId,
        start: { surah: form.startSurah, ayah: form.startAyah },
        ...(end ? { end: { surah: end.id, ayah: end.ayahCount } } : {}),
        direction: form.direction || undefined,
        memorizedSurahs,
        from: form.from,
        ...(own
          ? {
              custom: {
                unit: custom.unit,
                dailyAmount: custom.dailyAmount,
                near: custom.near > 0 ? { amount: custom.near, unit: custom.nearUnit } : null,
                far: custom.far > 0 ? { amount: custom.far, unit: custom.farUnit } : null,
                studyDays: custom.studyDays,
              },
            }
          : {}),
      };
      try {
        setPlan(
          await api<PlanPreviewResponse>("/v1/plans/preview", {
            method: "POST",
            json: { ...json, days: form.days },
          }),
        );
        setShown(json);
        setSavedId(null);
      } catch (err) {
        const details = err instanceof ApiError ? (err.body?.error.details as { missing?: string[] }) : null;
        if (details?.missing) {
          setPlan(null);
          setMissing(details.missing);
          setError(null);
          return;
        }
        throw err;
      }
    }, errorText);
  }

  const byDate = new Map<string, PlanPreviewItem[]>();
  for (const i of plan?.items ?? []) byDate.set(i.date, [...(byDate.get(i.date) ?? []), i]);

  return (
    <>
      <PageTitle
        title={plan ? `خطة ${plan.student.name}` : "معاينة خطة الطالب"}
        sub="اعرض الخطة، ثم احفظها مسودة واعتمدها. الخطة مفتوحة بلا مدة: تستمر يومًا بيوم حتى يُتم الطالب مقرره."
      />
      <PlansCard
        plans={plans.data?.plans}
        pending={plans.isPending}
        onApprove={setApproving}
        onResume={(p) =>
          void resume.run(async () => {
            // From today, or the day after the absence that paused it if that is today.
            const next = new Date(`${p.pausedSince}T00:00:00Z`);
            next.setUTCDate(next.getUTCDate() + 1);
            const from = today() > p.pausedSince! ? today() : next.toISOString().slice(0, 10);
            await api<PlanResponse>(`/v1/plans/${p.id}/resume`, { method: "POST", json: { from } });
            await qc.invalidateQueries({ queryKey: ["plans", enrollmentId] });
          }, errorText)
        }
      />
      <ErrorNote error={resume.error} />
      <ApproveModal
        plan={approving}
        onClose={() => setApproving(null)}
        onDone={() => void qc.invalidateQueries({ queryKey: ["plans", enrollmentId] })}
      />
      <section className="card">
        <form className="form section" onSubmit={submit}>
          <ErrorNote error={error} />
          {surahs.isPending ? (
            <Loading rows={2} />
          ) : (
            <>
              <div className="form-row">
                <Field id="pp-surah" label="يبدأ الحفظ الجديد من سورة">
                  <select
                    id="pp-surah"
                    value={form.startSurah}
                    onChange={(e) => setForm({ ...form, startSurah: Number(e.target.value), startAyah: 1 })}
                  >
                    {list.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.id}. {s.nameAr}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field id="pp-ayah" label={`من الآية (1–${maxAyah})`}>
                  <input
                    id="pp-ayah"
                    type="number"
                    dir="ltr"
                    min={1}
                    max={maxAyah}
                    value={form.startAyah}
                    onChange={(e) => setForm({ ...form, startAyah: Number(e.target.value) })}
                  />
                </Field>
              </div>
              <div className="form-row">
                <Field id="pp-dir" label="اتجاه الحفظ لهذا الطالب">
                  <select
                    id="pp-dir"
                    value={form.direction}
                    onChange={(e) => setForm({ ...form, direction: e.target.value as typeof form.direction })}
                  >
                    <option value="">حسب سياسة الحلقة</option>
                    <option value="backward">من الناس إلى البقرة</option>
                    <option value="forward">من البقرة إلى الناس</option>
                  </select>
                </Field>
                <Field id="pp-mem" label="محفوظ سابقًا (للمراجعة)" hint="من السورة المختارة حتى سورة الناس">
                  <select
                    id="pp-mem"
                    value={form.memorizedFrom}
                    onChange={(e) => setForm({ ...form, memorizedFrom: Number(e.target.value) })}
                  >
                    <option value={0}>لا شيء</option>
                    {list.map((s) => (
                      <option key={s.id} value={s.id}>
                        من {s.nameAr} إلى الناس
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <fieldset className="field own-program">
                <legend>برنامج الطالب</legend>
                <label className={`own-choice${own ? "" : " on"}`}>
                  <input type="radio" name="pp-own" checked={!own} onChange={() => setOwn(false)} />
                  <span>
                    <b>حسب إعدادات الحلقة</b>
                    <small>مقادير الحفظ والمراجعة وأيام الدراسة من سياسات الحلقة</small>
                  </span>
                </label>
                <label className={`own-choice${own ? " on" : ""}`}>
                  <input type="radio" name="pp-own" checked={own} onChange={() => setOwn(true)} />
                  <span>
                    <b>برنامج خاص لهذا الطالب</b>
                    <small>يغلب إعدادات الحلقة لهذا الطالب فقط</small>
                  </span>
                </label>
                {own && (
                  <>
                    <div className="form-row">
                      <AmountField
                        id="pp-daily"
                        label="الحفظ اليومي"
                        amount={custom.dailyAmount}
                        unit={custom.unit}
                        onChange={(dailyAmount, unit) => setCustom({ ...custom, dailyAmount, unit })}
                      />
                      <AmountField
                        id="pp-near"
                        label="المراجعة القريبة"
                        hint="من آخر محفوظه. 0: بلا مراجعة قريبة"
                        amount={custom.near}
                        unit={custom.nearUnit}
                        onChange={(near, nearUnit) => setCustom({ ...custom, near, nearUnit })}
                      />
                      <AmountField
                        id="pp-far"
                        label="المراجعة البعيدة"
                        hint="تدور على كل محفوظه. 0: بلا مراجعة بعيدة"
                        amount={custom.far}
                        unit={custom.farUnit}
                        onChange={(far, farUnit) => setCustom({ ...custom, far, farUnit })}
                      />
                    </div>
                    <fieldset className="field">
                      <legend>أيام الدراسة</legend>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                        {WEEKDAYS.map((d) => (
                          <label key={d} className="check">
                            <input
                              type="checkbox"
                              checked={custom.studyDays.includes(d)}
                              onChange={(e) =>
                                setCustom({
                                  ...custom,
                                  studyDays: e.target.checked
                                    ? WEEKDAYS.filter((x) => x === d || custom.studyDays.includes(x))
                                    : custom.studyDays.filter((x) => x !== d),
                                })
                              }
                            />
                            {weekdayLabel[d]}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </>
                )}
              </fieldset>
              <div className="form-row">
                <Field id="pp-from" label="تبدأ الخطة من">
                  <input
                    id="pp-from"
                    type="date"
                    dir="ltr"
                    value={form.from}
                    onChange={(e) => setForm({ ...form, from: e.target.value })}
                  />
                </Field>
                <Field id="pp-end" label="ينتهي المقرر عند" hint="الخطة مفتوحة حتى يُتم الطالب هذا المقرر">
                  <select
                    id="pp-end"
                    value={form.endSurah}
                    onChange={(e) => setForm({ ...form, endSurah: Number(e.target.value) })}
                  >
                    <option value={0}>حسب الاتجاه (البقرة أو الناس)</option>
                    {list.map((s) => (
                      <option key={s.id} value={s.id}>
                        نهاية سورة {s.nameAr}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="form-row">
                <Field id="pp-days" label="عرض الأيام" hint="للمعاينة فقط">
                  <select
                    id="pp-days"
                    value={form.days}
                    onChange={(e) => setForm({ ...form, days: Number(e.target.value) })}
                  >
                    <option value={7}>أسبوع</option>
                    <option value={14}>أسبوعان</option>
                    <option value={30}>شهر</option>
                  </select>
                </Field>
              </div>
              <div className="form-actions">
                <button
                  className="btn btn-primary"
                  disabled={busy || (own && (custom.dailyAmount <= 0 || custom.studyDays.length === 0))}
                >
                  {busy ? "جارٍ التوليد…" : "عرض الخطة"}
                </button>
              </div>
            </>
          )}
        </form>
      </section>

      {missing.length > 0 && (
        <section className="card">
          <div className="section">
            <p className="alert" role="alert">
              لا يمكن توليد الخطة قبل تحديد هذه السياسات لحلقة الطالب:{" "}
              {missing.map((k) => POLICY_META[k as PolicyKey]?.label ?? k).join("، ")}.
            </p>
            <Link to="/policies">الذهاب إلى سياسات البرامج</Link>
          </div>
        </section>
      )}

      {plan && (
        <>
          <section className="card">
            <div className="card-h">
              <h2>القواعد المستخدمة</h2>
              {plan.provisional ? (
                <Badge tone="warning">خطة أولية: بعض القيم غير معتمدة</Badge>
              ) : (
                <Badge tone="success">كل القيم معتمدة</Badge>
              )}
            </div>
            <dl className="kv">
              {plan.policies.map((p) => (
                <div key={p.key} style={{ display: "contents" }}>
                  <dt>{POLICY_META[p.key as PolicyKey]?.label ?? p.key}</dt>
                  <dd>
                    {formatPolicyValue(p.key as PolicyKey, p.value)}{" "}
                    <span className="muted small">
                      ({p.source}
                      {p.status === "approved" ? "، معتمد" : "، أولي"})
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
          </section>
          <section className="card">
            <div className="card-h">
              <h2>الأيام</h2>
              <span className="muted small">
                {plan.nextAyah ? `يبدأ ما بعدها من ${plan.nextAyah.label}` : "اكتمل نطاق الخطة"}
              </span>
            </div>
            <div className="section">
              <ErrorNote error={save.error} />
              {savedId ? (
                <p className="muted">حُفظت مسودة. اعتمدها من قائمة «خطط الطالب» أعلاه.</p>
              ) : (
                <button className="btn btn-primary" onClick={saveDraft} disabled={save.busy || !shown}>
                  {save.busy ? "جارٍ الحفظ…" : "حفظ الخطة مسودة"}
                </button>
              )}
            </div>
            {byDate.size === 0 ? (
              <Empty title="لا أيام دراسة في هذه المدة" />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>اليوم</th>
                      <th>النوع</th>
                      <th>المقرر</th>
                      <th>المقدار</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...byDate.entries()].flatMap(([date, items]) =>
                      items.map((i, n) => (
                        <tr key={`${date}-${i.kind}`}>
                          {n === 0 && (
                            <td rowSpan={items.length}>
                              <b>{dayName(date)}</b>
                              <div className="muted small num">{date}</div>
                            </td>
                          )}
                          <td>
                            <Badge tone={kindTone[i.kind]}>{kindLabel[i.kind]}</Badge>
                          </td>
                          <td>{i.ranges.map((r) => r.label).join("، ")}</td>
                          <td className="small">
                            {i.amount} {unitLabel[i.unit]}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

function PlansCard({
  plans,
  pending,
  onApprove,
  onResume,
}: {
  plans: PlanSummary[] | undefined;
  pending: boolean;
  onApprove: (p: PlanSummary) => void;
  onResume: (p: PlanSummary) => void;
}) {
  return (
    <section className="card">
      <div className="card-h">
        <h2>خطط الطالب</h2>
      </div>
      {pending ? (
        <Loading rows={2} />
      ) : !plans?.length ? (
        <Empty title="لا خطط محفوظة بعد" />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>الحالة</th>
                <th>بدأت</th>
                <th>المقرر</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td>
                    <Badge tone={statusTone[p.status]}>{statusLabel[p.status]}</Badge>{" "}
                    {p.provisional && <Badge tone="warning">أولية</Badge>}{" "}
                    {p.pausedSince && <Badge tone="danger">موقوفة منذ {p.pausedSince}</Badge>}
                    {p.proposedBy && <div className="muted small">مقترحة من المعلم: {p.proposedBy.name}</div>}
                  </td>
                  <td className="num small">{p.generatedFrom}</td>
                  <td className="small">
                    {p.start} ← {p.end}
                  </td>
                  <td>
                    {p.status === "approved" && p.pausedSince && (
                      <button className="btn btn-secondary btn-sm" onClick={() => onResume(p)}>
                        استئناف
                      </button>
                    )}
                    {p.status === "draft" && (
                      <button className="btn btn-secondary btn-sm" onClick={() => onApprove(p)}>
                        اعتماد
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ApproveModal({
  plan,
  onClose,
  onDone,
}: {
  plan: PlanSummary | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { busy, error, run } = useSubmit();
  return (
    <Modal title="اعتماد الخطة" open={!!plan} onClose={onClose}>
      <div className="form">
        <ErrorNote error={error} />
        <p>
          ستصبح هذه الخطة ({plan?.generatedFrom} ← {plan?.generatedUntil}) هي ما يراه المعلم والطالب، وتُحفظ
          الخطة المعتمدة السابقة كخطة سابقة.
        </p>
        {plan?.provisional && (
          <p className="alert">
            بعض القيم التي بُنيت عليها غير معتمدة من الجمعية بعد؛ ستظهر الخطة بعلامة «أولية».
          </p>
        )}
        <div className="form-actions">
          <button
            className="btn btn-primary"
            disabled={busy}
            onClick={() =>
              void run(async () => {
                await api<PlanResponse>(`/v1/plans/${plan!.id}/approve`, { method: "POST", json: {} });
                onDone();
                onClose();
              }, errorText)
            }
          >
            {busy ? "جارٍ الاعتماد…" : "اعتماد"}
          </button>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            تراجع
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** An amount and its unit, side by side. */
function AmountField({
  id,
  label,
  hint,
  amount,
  unit,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  amount: number;
  unit: AmountUnit;
  onChange: (amount: number, unit: AmountUnit) => void;
}) {
  return (
    <Field id={id} label={label} hint={hint}>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          id={id}
          type="number"
          dir="ltr"
          min={0}
          max={604}
          step={0.25}
          style={{ width: 90 }}
          value={amount}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0), unit)}
        />
        <select
          aria-label={`وحدة ${label}`}
          value={unit}
          onChange={(e) => onChange(amount, e.target.value as AmountUnit)}
        >
          {(Object.keys(unitLabel) as AmountUnit[]).map((u) => (
            <option key={u} value={u}>
              {unitLabel[u]}
            </option>
          ))}
        </select>
      </div>
    </Field>
  );
}
